import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface Notification {
  id: string;
  notification_uuid: string;
  notification_type: string;
  subtype?: string;
  environment: 'Sandbox' | 'Production';
  transaction_id?: string;
  product_id?: string;
  user_id?: string;
  created_at: string;
  processed: boolean;
}

interface NotificationCardProps {
  notification: Notification;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ notification }) => {
  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'SUBSCRIBED': '#34C759',
      'DID_RENEW': '#007AFF',
      'DID_FAIL_TO_RENEW': '#FF9500',
      'EXPIRED': '#8E8E93',
      'REFUND': '#FF3B30',
      'REVOKE': '#FF3B30'
    };
    return colors[type] || '#8E8E93';
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      'SUBSCRIBED': 'New',
      'DID_RENEW': 'Renewed',
      'DID_FAIL_TO_RENEW': 'Failed',
      'EXPIRED': 'Expired',
      'REFUND': 'Refunded',
      'REVOKE': 'Revoked'
    };
    return icons[type] || '•';
  };

  return (
    <div 
      className={`border rounded-lg p-4 transition-all hover:shadow-md ${
        notification.environment === 'Sandbox' ? 'border-l-4 border-l-orange-500' : 'border-l-4 border-l-green-500'
      }`}
      style={{ 
        borderColor: 'var(--rp-border)',
        backgroundColor: 'var(--rp-surface)'
      }}
    >
      <div className="flex justify-between items-start mb-3">
        <span 
          className="font-semibold text-sm flex items-center gap-2"
          style={{ color: getTypeColor(notification.notification_type) }}
        >
          <span>{getTypeIcon(notification.notification_type)}</span>
          <span>{notification.notification_type}</span>
        </span>
        <span 
          className="text-xs px-2 py-1 rounded-full"
          style={{ 
            backgroundColor: notification.environment === 'Production' ? 'rgba(52, 199, 89, 0.2)' : 'rgba(255, 149, 0, 0.2)',
            color: notification.environment === 'Production' ? '#34C759' : '#FF9500'
          }}
        >
          {notification.environment}
        </span>
      </div>
      
      <div className="space-y-1 text-sm mb-3" style={{ color: 'var(--rp-text-secondary)' }}>
        {notification.subtype && (
          <div className="text-xs">Subtype: {notification.subtype}</div>
        )}
        {notification.product_id && (
          <div>Product: {notification.product_id}</div>
        )}
        {notification.transaction_id && (
          <div className="font-mono text-xs">
            Transaction: {notification.transaction_id.slice(0, 16)}...
          </div>
        )}
      </div>
      
      <div className="flex justify-between items-center text-xs" style={{ color: 'var(--rp-text-tertiary)' }}>
        <span>{new Date(notification.created_at).toLocaleString()}</span>
        <span className={notification.processed ? 'text-green-500' : 'text-orange-500'}>
          {notification.processed ? 'Processed' : 'Pending'}
        </span>
      </div>
    </div>
  );
};

export const AppStoreNotificationsPanel: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [filter, setFilter] = useState<'all' | 'production' | 'sandbox'>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    
    // Subscribe to real-time updates
    const subscription = supabase
      .channel('app_store_notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'app_store_notifications'
        },
        (payload) => {
          setNotifications(prev => [payload.new as Notification, ...prev]);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function fetchNotifications() {
    setLoading(true);
    
    let query = supabase
      .from('app_store_notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(100);
    
    if (filter !== 'all') {
      query = query.eq('environment', filter === 'production' ? 'Production' : 'Sandbox');
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching notifications:', error);
    } else {
      setNotifications(data || []);
    }
    
    setLoading(false);
  }

  return (
    <div className="rp-bento-card">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold" style={{ color: 'var(--rp-text)' }}>
          App Store Server Notifications
        </h2>
        <button
          onClick={fetchNotifications}
          disabled={loading}
          className="px-4 py-2 rounded-lg border border-white/20 hover:bg-white/5 transition-all disabled:opacity-50"
          style={{ color: 'var(--rp-text)' }}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Filter Controls */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            filter === 'all' ? 'bg-blue-600 border-blue-600 text-white' : 'border-white/20 hover:bg-white/5'
          }`}
          style={filter !== 'all' ? { color: 'var(--rp-text)' } : {}}
        >
          All
        </button>
        <button
          onClick={() => setFilter('production')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            filter === 'production' ? 'bg-green-600 border-green-600 text-white' : 'border-white/20 hover:bg-white/5'
          }`}
          style={filter !== 'production' ? { color: 'var(--rp-text)' } : {}}
        >
          Production
        </button>
        <button
          onClick={() => setFilter('sandbox')}
          className={`px-4 py-2 rounded-lg border transition-all ${
            filter === 'sandbox' ? 'bg-orange-600 border-orange-600 text-white' : 'border-white/20 hover:bg-white/5'
          }`}
          style={filter !== 'sandbox' ? { color: 'var(--rp-text)' } : {}}
        >
          Sandbox
        </button>
      </div>

      {/* Notifications List */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-4"></div>
            <p style={{ color: 'var(--rp-text-secondary)' }}>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12" style={{ color: 'var(--rp-text-tertiary)' }}>
            No notifications yet
          </div>
        ) : (
          notifications.map(notification => (
            <NotificationCard key={notification.id} notification={notification} />
          ))
        )}
      </div>
    </div>
  );
};
